import "@/App.css";
import "./lib/i18n";
import "./lib/analysisLangInterceptor";
import { installBundleHealer } from "./lib/bundleHealer";
import { startLiveEventsSocket } from "./lib/liveEventsSocket";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "./pages/HomePage";
import StyleguidePage from "./pages/StyleguidePage";
import StyleguideV2Page from "./pages/StyleguideV2Page";
import StyleguideV3Page from "./pages/StyleguideV3Page";
import BetwayLeaguesPage from "./pages/BetwayLeaguesPage";
import TeamAliasesPage from "./pages/TeamAliasesPage";
import LandingPage from "./pages/LandingPage";
import { useAuth } from "./context/AuthContext";
import MatchDetailsPage from "./pages/MatchDetailsPage";
import HalftimePage from "./pages/HalftimePage";
import DoubleChancePage from "./pages/DoubleChancePage";
import SchedulePage from "./pages/SchedulePage";
import AccuracyPage from "./pages/AccuracyPage";
import LiveMatchPage from "./pages/LiveMatchPage";
import LiveNowPage from "./pages/LiveNowPage";
import LiveNextGoalPage from "./pages/LiveNextGoalPage";
import AsianHandicapPage from "./pages/AsianHandicapPage";
import GoalTimingPage from "./pages/GoalTimingPage";
import CoachPage from "./pages/CoachPage";
import HistoryPage from "./pages/HistoryPage";
import AuthPage from "./pages/AuthPage";
import AccountPage from "./pages/AccountPage";
import AutoBetPage from "./pages/AutoBetPage";
import AutopilotPage from "./pages/AutopilotPage";
import EarningsPage from "./pages/EarningsPage";
import AdminHealthPage from "./pages/AdminHealthPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminNudgeABPage from "./pages/AdminNudgeABPage";
import AdminSponsorsPage from "./pages/AdminSponsorsPage";
import AdminDataGapsPage from "./pages/AdminDataGapsPage";
import AnalyticsConfidenceTrendPage from "./pages/AnalyticsConfidenceTrendPage";
import SearchPage from "./pages/SearchPage";
import TeamPage from "./pages/TeamPage";
import DailyBetslipsPage from "./pages/DailyBetslipsPage";
import WeekendPage from "./pages/WeekendPage";
import TournamentHubPage from "./pages/TournamentHubPage";
import MyTeamsPage from "./pages/MyTeamsPage";
import TrackRecordSharePage from "./pages/TrackRecordSharePage";
import DoubleHistoryPage from "./pages/DoubleHistoryPage";
import WorldCupPage from "./pages/WorldCupPage";
import PrivacyPage from "./pages/PrivacyPage";
import EngineAdminPage from "./pages/EngineAdminPage";
import GoalPreviewPage from "./pages/GoalPreviewPage";
import GoldOverviewPage from "./pages/GoldOverviewPage";
import GoldDailyPicksPage from "./pages/GoldDailyPicksPage";
import GoldSchedulePage from "./pages/GoldSchedulePage";
import GoldDossierPage from "./pages/GoldDossierPage";
import GoldAccuracyPage from "./pages/GoldAccuracyPage";
import GoldMissAnalyzerPage from "./pages/GoldMissAnalyzerPage";
import GoldExpertPage from "./pages/GoldExpertPage";
import MobileBottomNav from "./components/MobileBottomNav";
import LivePushNotifier from "./components/LivePushNotifier";
import RequireAuth from "./components/RequireAuth";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { DesignModeProvider } from "./context/DesignModeContext";
import TerminalChrome from "./components/terminal/TerminalChrome";
import { BetSlipProvider } from "./context/BetSlipContext";
import { LiveSnapshotProvider } from "./context/LiveSnapshotContext";
import { BetSlipDrawer } from "./components/BetSlipDrawer";
import Footer from "./components/Footer";
import { Toaster } from "@/components/ui/sonner";

// Iter 87 — Resolve backend URL with same-origin awareness.
// When the frontend is served from a production domain (e.g.
// `ndakubet.com`), every browser request hitting `/api/*` is routed
// to the backend by the platform's reverse proxy.  Forcing the
// hard-coded `REACT_APP_BACKEND_URL` (which still points at the
// emergent.host preview/prod domain) creates avoidable cross-origin
// hops and 3rd-party-cookie issues that surface as "Network Error"
// during login.
//
// Resolution order:
//   1. If a runtime override exists at `window.__BACKEND_URL__`, use it.
//   2. If the current hostname is one of the configured "same-origin"
//      production domains, use the empty string so all `${API}/...`
//      calls resolve to same-origin paths like `/api/auth/login`.
//   3. Else fall back to the build-time `REACT_APP_BACKEND_URL`.
const _SAME_ORIGIN_HOSTS = new Set([
  "ndakubet.com",
  "www.ndakubet.com",
]);
const _resolveBackendUrl = () => {
  if (typeof window === "undefined") return process.env.REACT_APP_BACKEND_URL || "";
  if (window.__BACKEND_URL__) return window.__BACKEND_URL__;
  if (_SAME_ORIGIN_HOSTS.has(window.location.hostname)) return "";
  return process.env.REACT_APP_BACKEND_URL || "";
};
const BACKEND_URL = _resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Iter 110 — Self-heal stale bundles after a redeploy without forcing
// the user to manually clear cache.  Installed at module load so it's
// active before any chunk-load attempt.
installBundleHealer();

// Iter 110 — Real-time live-events WebSocket.  Idempotent — opens at
// most one connection per tab, auto-reconnects with exponential
// back-off, and dispatches incoming events as `ndaku-live-event`.
startLiveEventsSocket();

function App() {
  return (
    <ThemeProvider>
      <DesignModeProvider>
      <HelmetProvider>
      <div className="App bg-background text-foreground min-h-screen pb-bottomnav md:pb-0">
        <Toaster position="top-right" />
        <BrowserRouter>
          <AuthProvider>
            <LiveSnapshotProvider>
              <BetSlipProvider>
                <TerminalChrome />
                <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<RootGate />} />
                  <Route path="/goal-preview" element={<GoalPreviewPage />} />
                  <Route path="/styleguide" element={<StyleguidePage />} />
                  <Route path="/styleguide-v2" element={<StyleguideV2Page />} />
                  <Route path="/styleguide-v3" element={<StyleguideV3Page />} />
                  <Route path="/betway-leagues" element={<RequireAuth><BetwayLeaguesPage /></RequireAuth>} />
                  <Route path="/team-aliases" element={<RequireAuth><TeamAliasesPage /></RequireAuth>} />
                  <Route path="/gold" element={<RequireAuth><GoldOverviewPage /></RequireAuth>} />
                  <Route path="/gold/daily-picks" element={<RequireAuth><GoldDailyPicksPage /></RequireAuth>} />
                  <Route path="/gold/schedule" element={<RequireAuth><GoldSchedulePage /></RequireAuth>} />
                  <Route path="/gold/dossier" element={<RequireAuth><GoldDossierPage /></RequireAuth>} />
                  <Route path="/gold/accuracy" element={<RequireAuth><GoldAccuracyPage /></RequireAuth>} />
                  <Route path="/gold/miss-analyzer" element={<RequireAuth><GoldMissAnalyzerPage /></RequireAuth>} />
                  <Route path="/gold/expert" element={<RequireAuth><GoldExpertPage /></RequireAuth>} />
                  <Route path="/match/:matchId" element={<RequireAuth><MatchDetailsPage /></RequireAuth>} />
                  <Route path="/live" element={<RequireAuth><LiveNowPage /></RequireAuth>} />
                  <Route path="/live/:fixtureId" element={<RequireAuth><LiveMatchPage /></RequireAuth>} />
                  <Route path="/live-ai" element={<RequireAuth><LiveNextGoalPage /></RequireAuth>} />
                  <Route path="/live-ai/:fixtureId" element={<RequireAuth><LiveNextGoalPage /></RequireAuth>} />
                  <Route path="/coach" element={<RequireAuth><CoachPage /></RequireAuth>} />
                  <Route path="/coach/:slipId" element={<RequireAuth><CoachPage /></RequireAuth>} />
                  <Route path="/asian-handicap" element={<RequireAuth><AsianHandicapPage /></RequireAuth>} />
                  <Route path="/goal-timing" element={<RequireAuth><GoalTimingPage /></RequireAuth>} />
                  <Route path="/halftime" element={<RequireAuth><HalftimePage /></RequireAuth>} />
                  <Route path="/double-chance" element={<RequireAuth><DoubleChancePage /></RequireAuth>} />
                  <Route path="/schedule" element={<RequireAuth><SchedulePage /></RequireAuth>} />
                  <Route path="/world-cup" element={<RequireAuth><WorldCupPage /></RequireAuth>} />
                  <Route path="/accuracy" element={<RequireAuth><AccuracyPage /></RequireAuth>} />
                  <Route path="/history" element={<RequireAuth><HistoryPage /></RequireAuth>} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
                  <Route path="/auto-bet" element={<RequireAuth><AutoBetPage /></RequireAuth>} />
                  <Route path="/autopilot" element={<RequireAuth><AutopilotPage /></RequireAuth>} />
                  <Route path="/account/betslips" element={<RequireAuth><DailyBetslipsPage /></RequireAuth>} />
                  <Route path="/account/earnings" element={<RequireAuth><EarningsPage /></RequireAuth>} />
                  <Route path="/admin/health" element={<RequireAuth><AdminHealthPage /></RequireAuth>} />
                  <Route path="/admin/users" element={<RequireAuth><AdminUsersPage /></RequireAuth>} />
                  <Route path="/admin/nudge-ab" element={<RequireAuth><AdminNudgeABPage /></RequireAuth>} />
                  <Route path="/admin/sponsors" element={<RequireAuth><AdminSponsorsPage /></RequireAuth>} />
                  <Route path="/admin/data-gaps" element={<RequireAuth><AdminDataGapsPage /></RequireAuth>} />
                  <Route path="/analytics/confidence-trend" element={<RequireAuth><AnalyticsConfidenceTrendPage /></RequireAuth>} />
                  <Route path="/search" element={<RequireAuth><SearchPage /></RequireAuth>} />
                  <Route path="/team/:slug" element={<RequireAuth><TeamPage /></RequireAuth>} />
                  <Route path="/weekend" element={<RequireAuth><WeekendPage /></RequireAuth>} />
                  <Route path="/tournaments" element={<RequireAuth><TournamentHubPage /></RequireAuth>} />
                  <Route path="/my-teams" element={<RequireAuth><MyTeamsPage /></RequireAuth>} />
                  <Route path="/share/:date" element={<TrackRecordSharePage />} />
                  <Route path="/double/history" element={<DoubleHistoryPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/admin/engines" element={<RequireAuth><EngineAdminPage /></RequireAuth>} />
                </Routes>
                </ErrorBoundary>
                <BetSlipDrawer />
                <LivePushNotifier />
                <MobileBottomNav />
                <Footer />
              </BetSlipProvider>
            </LiveSnapshotProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
      </HelmetProvider>
      </DesignModeProvider>
    </ThemeProvider>
  );
}

export default App;

// Iter 83 — Hard-wall gate: anonymous → marketing splash, authenticated → full homepage.
function RootGate() {
  // useAuth must be called inside the AuthProvider; this component is
  // rendered as a child of <AuthProvider> so it's safe.
  const { token, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground" />
      </div>
    );
  }
  return token ? <HomePage /> : <LandingPage />;
}
